import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { useThemeBySlug, useThemeExercises } from '@/lib/themes/themes-queries';
import type { ThemeExerciseListItem } from '@/lib/themes/themes-api';
import { groupThemeExercises } from '@/lib/themes/theme-exercise-groups';
import { mdExcerpt } from '@/lib/themes/md-excerpt';
import { MarkdownMath } from '@jeraisonne/ui';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
};

const DIFFICULTY_CLASS: Record<string, string> = {
  easy: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  hard: 'bg-rose-100 text-rose-800',
};

type TabId = 'description' | 'exercises';

export function ThemeDetailPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const navigate = useNavigate();
  const { data: theme, isLoading } = useThemeBySlug(slug);
  const { data: exercises, isLoading: exosLoading } = useThemeExercises(theme?.id);
  const [tab, setTab] = useState<TabId>('description');

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Chargement…</p>
      </div>
    );
  }
  if (!theme) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Thème introuvable ou inaccessible.</p>
        <Link to="/themes" className="text-primary-600 mt-3 inline-block text-sm font-semibold">
          ← Retour
        </Link>
      </div>
    );
  }

  function goToExercise(ex: ThemeExerciseListItem) {
    navigate({
      to: '/learn/$subject/$level/$chapter/$exercise',
      params: {
        subject: ex.chapter.subject,
        level: ex.chapter.level,
        chapter: ex.chapter.slug,
        exercise: ex.id,
      },
    });
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="flex flex-col gap-3">
        <div className="bg-ink-100 relative aspect-video w-full overflow-hidden rounded-2xl">
          {theme.illustration_url && (
            <img
              src={theme.illustration_url}
              alt={theme.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          <div className="bg-linear-to-t absolute inset-0 from-black/55 via-black/10 to-black/30" />

          <Link
            to="/themes"
            aria-label="Retour aux thèmes"
            className="text-ink-900 absolute left-3 top-3 inline-flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-full bg-white/90 backdrop-blur transition hover:bg-white active:scale-[0.96]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>

          <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
            <span className="text-ink-800 rounded-full bg-white/90 px-3 py-1 text-xs font-medium backdrop-blur">
              {theme.min_level
                ? `À partir de la ${LEVEL_LABEL[theme.min_level] ?? theme.min_level}`
                : 'Tous niveaux'}
            </span>
            <span className="text-ink-800 rounded-full bg-white/90 px-3 py-1 text-xs font-medium backdrop-blur">
              {theme.exercise_count} exercice{theme.exercise_count > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <h1 className="font-display text-ink-900 text-[24px] font-bold sm:text-[28px]">
          {theme.title}
        </h1>
        {theme.short_description && (
          <p className="text-ink-600 font-sans text-[14px]">{theme.short_description}</p>
        )}
      </header>

      <div
        role="tablist"
        aria-label="Sections du thème"
        className="border-ink-200 mt-6 flex gap-1 border-b"
      >
        <TabButton active={tab === 'description'} onClick={() => setTab('description')}>
          Description
        </TabButton>
        <TabButton active={tab === 'exercises'} onClick={() => setTab('exercises')}>
          Exercices
          {exercises && exercises.length > 0 && (
            <span className="bg-ink-100 text-ink-700 ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold">
              {exercises.length}
            </span>
          )}
        </TabButton>
      </div>

      {tab === 'description' && (
        <section role="tabpanel" className="mt-5">
          {theme.description_md ? (
            <div className="border-ink-200 rounded-2xl border bg-white p-5">
              <MarkdownMath>{theme.description_md}</MarkdownMath>
            </div>
          ) : (
            <p className="text-ink-500 text-sm">Aucune description pour ce thème.</p>
          )}
        </section>
      )}

      {tab === 'exercises' && (
        <section role="tabpanel" className="mt-5">
          {exosLoading ? (
            <p className="text-ink-500 text-sm">Chargement des exercices…</p>
          ) : !exercises || exercises.length === 0 ? (
            <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
              <p className="text-ink-700 text-sm">
                Aucun exercice n’est encore rattaché à ce thème.
              </p>
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {groupThemeExercises(exercises).flatMap((group) =>
                group.exercises.map((ex) => {
                  const context = ex.context_md?.trim();
                  return (
                    <li key={ex.id}>
                      <button
                        type="button"
                        onClick={() => goToExercise(ex)}
                        className="border-ink-200 hover:border-ink-300 group flex h-full w-full flex-col overflow-hidden rounded-2xl border bg-white text-left transition active:scale-[0.98]"
                      >
                        {/* Cover : illustration + pastille « Commencer » posée dessus */}
                        <div className="bg-ink-100 relative aspect-video w-full shrink-0 overflow-hidden">
                          {ex.illustration_url ? (
                            <img
                              src={ex.illustration_url}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="from-primary-100 to-primary-50 bg-linear-to-br h-full w-full" />
                          )}
                          <span className="bg-primary-500 group-hover:bg-primary-600 absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-md transition-colors">
                            Commencer
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                        <div className="flex flex-1 flex-col gap-1.5 p-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="bg-ink-900 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                              {LEVEL_LABEL[ex.chapter.level] ?? ex.chapter.level}
                            </span>
                            <span
                              className={cn(
                                'rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                                DIFFICULTY_CLASS[ex.difficulty] ?? 'bg-ink-100 text-ink-700',
                              )}
                            >
                              {DIFFICULTY_LABEL[ex.difficulty] ?? ex.difficulty}
                            </span>
                          </div>
                          <p className="text-ink-900 line-clamp-2 text-sm font-semibold">
                            {ex.title}
                          </p>
                          {context && (
                            <p className="text-ink-500 line-clamp-2 text-xs">
                              {mdExcerpt(context, 200)}
                            </p>
                          )}
                          <span className="text-ink-400 mt-auto pt-1 text-[11px]">
                            {ex.chapter.title}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                }),
              )}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'font-display relative inline-flex min-h-[2.75rem] shrink-0 items-center px-4 text-[14px] font-semibold transition-colors',
        active ? 'text-primary-700' : 'text-ink-500 hover:text-ink-800',
      )}
    >
      {children}
      {active && (
        <span className="bg-primary-500 absolute inset-x-2 -bottom-px h-0.5 rounded-full" />
      )}
    </button>
  );
}
